import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AttributeComponent } from '@hub4b/controls/attribute-form-item/attribute.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { AvmAttributeFieldPipe } from 'src/app/core/pipes/avm-attribute-field.pipe';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { BoxGroupInfos } from 'src/app/models/box-group-infos';
import { BoxInfos } from 'src/app/models/box-infos';
import { Dictionary } from 'src/app/models/core/dictionary_T';
import { PropertyType } from 'src/app/models/enums/property-type';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { Colors } from 'src/app/models/server/enums/colors';
import { LockedType } from 'src/app/models/server/enums/locked-type';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { Folder } from 'src/app/models/server/folder';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { AttributeDefinitionsService } from 'src/app/services/http/attribute-definitions.service';
import { UsersService } from 'src/app/services/http/users.service';
import { BoxesStateService } from 'src/app/services/state/boxes-state.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { DialogAddFolderData } from './models/dialog-add-folder-data';

@Component({
    selector: 'app-add-folder',
    templateUrl: './add-folder.component.html',
    styleUrls: ['./add-folder.component.scss'],
    imports: [MatCardModule, AttributeComponent, MatFormFieldModule, MatSelectModule, MatOptionModule, NgxSpinnerModule, MatButtonModule]
})
export class AddFolderComponent extends BaseDirective implements OnInit {
    /**
     * Références des Enums utilisées dans le template.
     */
    PropertyType = PropertyType;
    ViewType = ViewType;
    Colors = Colors;

    /**
     * Références des constantes utilisées dans le template.
     */
    public VIEW_ID = FolderPropertyCodes.VIEW_ID;
    public BOX_ID = FolderPropertyCodes.BOX_ID;

    /**
     * Liste des définitions des attributs systèmes.
     */
    public attributesSystem: AttributeDefinition[];

    /**
     * Liste des définitions des attributs personnalisés.
     */
    public attributesCustom: AttributeDefinition[];

    /**
     * Liste des groupes de bannettes.
     */

    public boxGroupInfo: BoxGroupInfos[];

    /**
     * Liste des bannettes.
     */
    public boxes: BoxInfos[];

    private configurationLoadingStatus: Dictionary<boolean>;
    private attributesConfigurationKey = 'attributesConfiguration';

    protected onConfigurationLoaded$: Subject<void> = new Subject<void>();

    /**
     * Constructeur.
     * @param usersService
     * @param connectedUserStateService
     * @param boxesStateService
     * @param workflowsStateService
     * @param dialogRef
     * @param data : Données transitant entre l'appelant de la boite de dialogue et la boite de dialogue (En entrée et en sortie)
     * @param datePipe
     * @param displayLabelHelper
     */
    constructor(
        private usersService: UsersService,
        private connectedUserStateService: ConnectedUsersStateService,
        private boxesStateService: BoxesStateService,
        private viewsStateService: ViewsStateService,
        private attributesService: AttributeDefinitionsService,
        public enumHelper: EnumHelper,
        private attributeFieldPipe: AvmAttributeFieldPipe,
        protected notificationService: NotificationService,
        public dialogRef: MatDialogRef<AddFolderComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogAddFolderData,

        private spinner: SpinnerService
    ) {
        super();
    }

    ngOnInit(): void {
        this.configurationLoadingStatus = new Dictionary<boolean>();
        this.configurationLoadingStatus.set(this.attributesConfigurationKey, false);

        this.spinner.show('add-folder-spinner');
        // Initialise le dossier.
        this.data.folder = new Folder();
        this.data.folder.id = -1;
        this.data.folder.expanded = false;
        this.data.folder.organizationId = this.connectedUserStateService.getUserConnected().organization.id;
        this.data.folder.lockedType = LockedType.NotLocked;
        this.data.folder.isRead = false;
        this.data.folder.creationDate = new Date();
        this.data.folder.updateDate = new Date();
        this.data.folder.color = Colors.None;
        this.data.folder.workflowStepsSnapshot = [];
        this.data.folder.isDelete = false;
        this.data.folder.hasDocument = false;
        this.data.folder.hasComment = false;
        this.data.folder.isInStandByState = false;
        this.data.folder.archivingDate = null;
        this.data.folder.customAttributes = {};

        if (this.data.selectorType == this.ViewType.Workflow) {
            this.FillFolderWithCurrentView();
        }

        // Récupère la liste des groupes de bannettes et les bannettes.
        this.boxGroupInfo = this.boxesStateService.getBoxesGroupsList();
        this.boxes = this.boxesStateService.getBoxesList();

        this.onConfigurationLoaded$.subscribe((x) => {
            if (this.configurationLoadingStatus.get(this.attributesConfigurationKey) === true) {
                this.spinner.hide('add-folder-spinner');
            }
        });

        this.loadAttributeDefinitions();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur met à jour un attribut du dossier.
     * @param attributeDefinition : Définition de l'attribut modifié.
     * @param value : Valeur de l'attribut.
     */
    public updateAttribute(attributeDefinition: AttributeDefinition, value: any): void {
        if (attributeDefinition.custom == false) {
            this.data.folder[attributeDefinition.propertyCode] = value;
        } else {
            this.data.folder.customAttributes[attributeDefinition.propertyCode] = value;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public validate(): void {
        this.attributesCustom
            .filter((x) => x.type === PropertyType.Color && x.custom === true)
            .forEach((attribute) => {
                this.data.folder.customAttributes[attribute.propertyCode] = this.enumHelper.convertColor(this.data.folder.customAttributes[attribute.propertyCode]);
            });

        this.dialogRef.close(this.data);
    }

    /**
     * Retourne une valeur indiquant si le formulaire est valide.
     * Utilisé pour rendre actif le bouton valider.
     * @returns True si le formulaire est valide; false autrement.
     */
    public isValid(): boolean {
        let isValid = true;

        this.attributesSystem.forEach((element) => {
            if (!this.isValidAttribute(element)) {
                isValid = false;
            }
        });

        this.attributesCustom.forEach((element) => {
            if (!this.isValidAttribute(element)) {
                isValid = false;
            }
        });

        return isValid;
    }

    /**
     * Retourne une valeur indiquant si l'attribut est valide.
     * @param attributeDefinition : Definition de l'attribut à valider.
     * @returns True si l'attribut est valide; false autrement.
     */
    private isValidAttribute(attributeDefinition: AttributeDefinition): boolean {
        let isValid = true;

        if (
            attributeDefinition.mandatory &&
            (!!!this.attributeFieldPipe.transform(this.data.folder, attributeDefinition) || this.attributeFieldPipe.transform(this.data.folder, attributeDefinition) === '')
        ) {
            isValid = false;
        } else if (
            !!attributeDefinition.format &&
            !!this.attributeFieldPipe.transform(this.data.folder, attributeDefinition) &&
            this.attributeFieldPipe.transform(this.data.folder, attributeDefinition) !== ''
        ) {
            let pattern = new RegExp(attributeDefinition.format);

            if (!pattern.test(this.attributeFieldPipe.transform(this.data.folder, attributeDefinition))) {
                isValid = false;
            }
        }

        return isValid;
    }

    /**
     * Charge les définitions d'attributs en mémoire.
     */
    private loadAttributeDefinitions() {
        this.attributesService
            .List(this.viewsStateService.getSelectedView().id)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                x.forEach((element) => {
                    if (element.propertyCode == 'folderStateId') {
                        // Dans le cas où il n'y a pas d'état de dossier
                        if (!element.values) {
                            this.notificationService.displayWarning(
                                "Ajout d'un dossier",
                                "La création d'un dossier nécessite la présence d'états. Veuillez contacter votre administrateur pour qu'il les crée."
                            );
                            this.dialogRef.close();
                        }

                        this.data.folder.folderStateId = element.values[0].value;
                    }
                });

                const removeItem = this.data.selectorType == ViewType.Box ? FolderPropertyCodes.VIEW_ID : FolderPropertyCodes.BOX_ID;

                this.attributesSystem = x.filter((a) => a.displayedInCreationForm === true && !a.custom && a.propertyCode != removeItem);
                this.attributesCustom = x.filter((a) => a.displayedInCreationForm === true && a.custom);

                // Met à jour les valeurs par défaut des attributs personnalisées
                this.attributesCustom
                    .filter((x) => x.defaultValue !== null && x.defaultValue !== '')
                    .forEach((attribute) => {
                        this.updateAttribute(attribute, attribute.type === PropertyType.Color ? this.enumHelper.convertColor(attribute.defaultValue) : attribute.defaultValue);
                    });

                // Met à jour les valeurs par défaut des attributs systèmes
                this.attributesSystem
                    .filter((x) => x.defaultValue !== null && x.defaultValue !== '')
                    .forEach((attribute) => {
                        this.updateAttribute(attribute, attribute.type === PropertyType.Color ? this.enumHelper.convertColor(attribute.defaultValue) : attribute.defaultValue);
                    });

                this.configurationLoadingStatus.set(this.attributesConfigurationKey, true);
                this.onConfigurationLoaded$.next();
            });
    }

    /**
     * Alimente la propriété vue du dossier avec la vue courante.
     */
    private FillFolderWithCurrentView() {
        const selectedView = this.viewsStateService.getSelectedView();

        if (selectedView) {
            this.data.folder.viewId = selectedView.id;
        }
    }
}
